import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, Upload, CheckCircle2, Loader2, AlertTriangle, Phone, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicCleaning() {
  const { token } = useParams();
  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [checked, setChecked] = useState({}); // key `${roomIdx}:${item}` -> bool
  const [notes, setNotes] = useState("");
  const [hasIssue, setHasIssue] = useState(false);
  const [issueDesc, setIssueDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [roomPhotos, setRoomPhotos] = useState({}); // roomIdx -> [dataurl]
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/clean/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [token]);

  const readFiles = (fileList, max) => Promise.all([...fileList].slice(0, max).map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); })));
  const onPhoto = async (e) => { const arr = await readFiles(e.target.files, 6); setPhotos((p) => [...p, ...arr].slice(0, 6)); };
  const onRoomPhoto = async (ri, e) => { const arr = await readFiles(e.target.files, 3); setRoomPhotos((p) => ({ ...p, [ri]: [...(p[ri] || []), ...arr].slice(0, 3) })); };

  // Normalise into rooms; fall back to a single General room from flat items.
  const rooms = (info && info.rooms && info.rooms.length > 0)
    ? info.rooms
    : (info && info.items ? [{ name: "General", items: info.items }] : []);

  const totalItems = rooms.reduce((n, r) => n + (r.items || []).length, 0);
  const doneItems = rooms.reduce((n, r, ri) => n + (r.items || []).filter((it) => checked[`${ri}:${it}`]).length, 0);

  const submit = async () => {
    setSending(true); setErr("");
    const payload = {
      cleaner_name: name, cleaner_company: company,
      rooms: rooms.map((r, ri) => ({ name: r.name, items: (r.items || []).map((it) => ({ name: it, done: !!checked[`${ri}:${it}`] })), photos: roomPhotos[ri] || [] })),
      notes, has_issue: hasIssue, issue_description: hasIssue ? issueDesc : "",
      photos: hasIssue ? photos : [],
    };
    try { await axios.post(`${API}/public/clean/${token}`, payload); setDone(true); }
    catch (e) {
      setSending(false);
      setErr(e.response?.status === 429
        ? (e.response?.data?.detail || "Too many submissions just now — please wait a few minutes.")
        : "Sorry, we couldn't submit just now. Please try again.");
    }
  };

  if (info === undefined) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (info === null) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] p-6"><p className="text-center text-muted-foreground">This cleaning link is invalid or has expired.</p></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md bg-[#18181A] flex items-center justify-center"><Sparkles className="h-5 w-5 text-white" /></div>
          <span className="font-head text-xl font-extrabold tracking-tight">{info.brand_name}</span>
        </div>
        {done ? (
          <div className="bg-white border border-border rounded-md p-8 text-center" data-testid="clean-thanks">
            <CheckCircle2 className="h-10 w-10 text-[#2B4C3F] mx-auto mb-3" />
            <h1 className="font-head text-2xl font-extrabold">All logged</h1>
            <p className="text-sm text-muted-foreground mt-2">Thanks — your cleaning of <strong>{info.property_title}</strong> has been recorded for the team.</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md p-6">
            <p className="overline text-accent">Cleaning checklist</p>
            <h1 className="font-head text-2xl font-extrabold tracking-tight mt-1">{info.property_title}</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Work through each room and tick tasks as you complete them, then submit. Only add a photo if you're reporting a major issue.</p>
            {info.office_phone && (
              <a href={`tel:${info.office_phone}`} data-testid="clean-call-office" className="inline-flex items-center gap-2 mb-5 text-sm font-medium px-4 py-2 rounded-full bg-[#18181A] text-white hover:opacity-90 transition-opacity">
                <Phone className="h-4 w-4" />Call office
              </a>
            )}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="overline">Your name</Label><Input data-testid="clean-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /></div>
                <div><Label className="overline">Company (optional)</Label><Input data-testid="clean-company" value={company} onChange={(e) => setCompany(e.target.value)} className="mt-1.5" /></div>
              </div>

              {totalItems > 0 && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Progress</span>
                  <span data-testid="clean-progress" className="font-medium text-foreground">{doneItems}/{totalItems} done</span>
                </div>
              )}

              {rooms.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground border border-border rounded-md">No checklist items set for this property.</p>}

              {rooms.map((room, ri) => {
                const items = room.items || [];
                const roomDone = items.filter((it) => checked[`${ri}:${it}`]).length;
                return (
                  <div key={ri} data-testid={`clean-room-${ri}`} className="border border-border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[#2B4C3F]/[0.06] border-b border-border">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 text-[#2B4C3F]" />
                        <span className="font-head font-bold text-sm tracking-tight">{room.name}</span>
                      </div>
                      <span className={`text-[0.65rem] px-2 py-0.5 rounded-full font-medium ${roomDone === items.length && items.length > 0 ? "bg-[#2B4C3F] text-white" : "bg-white text-muted-foreground border border-border"}`}>{roomDone}/{items.length}</span>
                    </div>
                    <div className="divide-y divide-border">
                      {items.map((it) => {
                        const key = `${ri}:${it}`;
                        return (
                          <label key={key} data-testid={`clean-item-${ri}-${it}`} className="flex items-center gap-3 px-4 py-3 text-sm cursor-pointer hover:bg-secondary/40 transition-colors">
                            <Checkbox checked={!!checked[key]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [key]: !!v }))} data-testid={`clean-check-${ri}-${it}`} />
                            <span className={checked[key] ? "line-through text-muted-foreground" : ""}>{it}</span>
                          </label>
                        );
                      })}
                      {items.length === 0 && <p className="px-4 py-3 text-xs text-muted-foreground">No tasks in this room.</p>}
                    </div>
                    <div className="px-4 py-2.5 border-t border-border bg-secondary/20">
                      <label className="inline-flex items-center gap-2 text-xs underline cursor-pointer text-muted-foreground"><Camera className="h-3.5 w-3.5" />Add a done photo (optional)<input type="file" accept="image/*" multiple className="hidden" data-testid={`clean-room-photo-${ri}`} onChange={(e) => onRoomPhoto(ri, e)} /></label>
                      {(roomPhotos[ri] || []).length > 0 && <div className="flex gap-2 flex-wrap mt-2">{roomPhotos[ri].map((p, i) => <img key={i} src={p} alt="" className="h-14 w-14 object-cover rounded-md border border-border" />)}</div>}
                    </div>
                  </div>
                );
              })}

              <div><Label className="overline">Notes (optional)</Label><Textarea data-testid="clean-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" placeholder="Anything the team should know…" /></div>

              <div className="bg-[#D98324]/5 border border-[#D98324]/25 rounded-md p-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer font-medium">
                  <Checkbox checked={hasIssue} onCheckedChange={(v) => setHasIssue(!!v)} data-testid="clean-has-issue" />
                  <AlertTriangle className="h-4 w-4 text-[#D98324]" /> Report a major issue / repair
                </label>
                {hasIssue && (
                  <div className="mt-3 space-y-3">
                    <Textarea data-testid="clean-issue-desc" rows={3} value={issueDesc} onChange={(e) => setIssueDesc(e.target.value)} placeholder="Describe the issue…" />
                    <div>
                      <label className="flex items-center gap-2 text-sm underline cursor-pointer"><Upload className="h-4 w-4" />Add a photo<input type="file" accept="image/*" multiple className="hidden" data-testid="clean-photos" onChange={onPhoto} /></label>
                      {photos.length > 0 && <div className="flex gap-2 flex-wrap mt-2">{photos.map((p, i) => <img key={i} src={p} alt="" className="h-16 w-16 object-cover rounded-md border border-border" />)}</div>}
                    </div>
                  </div>
                )}
              </div>

              <Button data-testid="clean-submit" className="w-full" disabled={sending || !name || (hasIssue && !issueDesc)} onClick={submit}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit cleaning"}
              </Button>
              {err && <p data-testid="clean-error" className="text-xs text-[#9E382A] text-center">{err}</p>}
            </div>
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground mt-4">Powered by {info.brand_name}</p>
      </div>
    </div>
  );
}
